import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../Components/ui/card";
import { Button } from "../Components/ui/button";
import { Input } from "../Components/ui/input";
import { Textarea } from "../Components/ui/textarea";
import { Badge } from "../Components/ui/badge";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  Mail, 
  MapPin, 
  Send,
  CheckCircle,
  MessageCircle,
  Loader2
} from "lucide-react";

export default function Contact() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const copy = t("contact");
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    phoneNumber: '',
    country: '',
    serviceInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [qrCodeLoaded, setQrCodeLoaded] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    // All fields are required
    if (!formData.fullName.trim()) {
      newErrors.fullName = copy.validation.required;
    }
    
    if (!formData.email.trim()) {
      newErrors.email = copy.validation.required;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = copy.validation.email;
    }
    
    if (!formData.company.trim()) {
      newErrors.company = copy.validation.required;
    }
    
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = copy.validation.required;
    }
    
    if (!formData.country.trim()) {
      newErrors.country = copy.validation.required;
    }
    
    if (!formData.serviceInterest) {
      newErrors.serviceInterest = copy.validation.inquiry;
    }
    
    if (!formData.message.trim()) {
      newErrors.message = copy.validation.required;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleWhatsAppClick = () => {
    // WhatsApp Business API URL with phone number and message
    const whatsappNumber = "18975155749";
    const message = "Hello, I want to know more";
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp in a new window
    window.open(whatsappUrl, '_blank');
  };

  const handleQrCodeLoad = () => {
    // Delay a bit to show, creating a progressive effect
    setTimeout(() => {
      setQrCodeLoaded(true);
    }, 300);
  };

  const handleQrCodeError = (e) => {
    // Fallback to PNG if WebP is not supported
    e.target.src = '/contact.png';
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        const response = await fetch('/api/system/submit-image-form', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.fullName,
            email: formData.email,
            company: formData.company,
            phone: formData.phoneNumber,
            country: formData.country,
            serviceInterest: formData.serviceInterest,
            message: formData.message,
            submittedAt: new Date().toISOString()
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          setIsSubmitted(true);
          // Reset form
          setFormData({
            fullName: '',
            email: '',
            company: '',
            phoneNumber: '',
            country: '',
            serviceInterest: '',
            message: ''
          });
          setErrors({});
          // Redirect to thank you page after 1.5 seconds
          setTimeout(() => {
            navigate('/thank-you');
            // Ensure page scrolls to top after navigation
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
          }, 1500);
        } else {
          setErrors({ submit: copy.errors.submit });
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setErrors({ submit: copy.errors.network });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors(prev => ({
        ...prev,
        [e.target.name]: ''
      }));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="contact-hero" className="py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-4 border-emerald-600 text-emerald-600 animate-bounce-in">
            {copy.heroBadge}
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 animate-slide-in-up">
            {copy.heroTitleLead}
            <span className="text-gradient block animate-gradient-shift">{copy.heroTitleAccent}</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in">
            {copy.heroBody}
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg animate-slide-in-left">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-900">
                    {copy.formTitle}
                  </CardTitle>
                  <p className="text-gray-600">
                    {copy.formBody}
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {copy.fields.fullName}
                        </label>
                        <Input
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder={copy.placeholders.fullName}
                          required
                          onInvalid={(e) => e.target.setCustomValidity(copy.validation.required)}
                          onInput={(e) => e.target.setCustomValidity('')}
                          className={errors.fullName ? 'border-red-500 focus:border-red-500' : ''}
                        />
                        {errors.fullName && (
                          <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {copy.fields.email}
                        </label>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder={copy.placeholders.email}
                          required
                          onInvalid={(e) => e.target.setCustomValidity(copy.validation.email)}
                          onInput={(e) => e.target.setCustomValidity('')}
                          className={errors.email ? 'border-red-500 focus:border-red-500' : ''}
                        />
                        {errors.email && (
                          <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {copy.fields.company}
                        </label>
                        <Input
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder={copy.placeholders.company}
                          required
                          onInvalid={(e) => e.target.setCustomValidity(copy.validation.required)}
                          onInput={(e) => e.target.setCustomValidity('')}
                          className={errors.company ? 'border-red-500 focus:border-red-500' : ''}
                        />
                        {errors.company && (
                          <p className="text-red-500 text-sm mt-1">{errors.company}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {copy.fields.phone}
                        </label>
                        <Input
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          placeholder={copy.placeholders.phone}
                          required
                          onInvalid={(e) => e.target.setCustomValidity(copy.validation.required)}
                          onInput={(e) => e.target.setCustomValidity('')}
                          className={errors.phoneNumber ? 'border-red-500 focus:border-red-500' : ''}
                        />
                        {errors.phoneNumber && (
                          <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {copy.fields.country}
                      </label>
                      <Input
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder={copy.placeholders.country}
                        required
                        onInvalid={(e) => e.target.setCustomValidity(copy.validation.required)}
                        onInput={(e) => e.target.setCustomValidity('')}
                        className={errors.country ? 'border-red-500 focus:border-red-500' : ''}
                      />
                      {errors.country && (
                        <p className="text-red-500 text-sm mt-1">{errors.country}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        {copy.fields.inquiry}
                      </label>
                      <div className="space-y-4">
                        <div 
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                            formData.serviceInterest === 'Technical Support' 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-gray-200 hover:border-emerald-300'
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, serviceInterest: 'Technical Support' }))}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-4 h-4 rounded-full border-2 ${
                              formData.serviceInterest === 'Technical Support' 
                                ? 'border-emerald-500 bg-emerald-500' 
                                : 'border-gray-300'
                            }`}>
                              {formData.serviceInterest === 'Technical Support' && (
                                <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{copy.inquiry.generalTitle}</div>
                              <div className="text-sm text-gray-600">{copy.inquiry.generalDescription}</div>
                            </div>
                          </div>
                        </div>
                        
                        <div 
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                            formData.serviceInterest === 'Business Partnership' 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-gray-200 hover:border-emerald-300'
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, serviceInterest: 'Business Partnership' }))}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-4 h-4 rounded-full border-2 ${
                              formData.serviceInterest === 'Business Partnership' 
                                ? 'border-emerald-500 bg-emerald-500' 
                                : 'border-gray-300'
                            }`}>
                              {formData.serviceInterest === 'Business Partnership' && (
                                <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{copy.inquiry.businessTitle}</div>
                              <div className="text-sm text-gray-600">{copy.inquiry.businessDescription}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {errors.serviceInterest && (
                        <p className="text-red-500 text-sm mt-2">{errors.serviceInterest}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {copy.fields.message}
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={copy.placeholders.message}
                        rows={6}
                        required
                        onInvalid={(e) => e.target.setCustomValidity(copy.validation.required)}
                        onInput={(e) => e.target.setCustomValidity('')}
                        className={errors.message ? 'border-red-500 focus:border-red-500' : ''}
                      />
                      {errors.message && (
                        <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                      )}
                    </div>

                    {/* Success Message */}
                    {isSubmitted && (
                      <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                          <div>
                            <h3 className="text-sm font-medium text-green-800">{copy.success.title}</h3>
                            <p className="text-sm text-green-600 mt-1">{copy.success.body}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Error Message */}
                    {errors.submit && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-5 h-5 text-red-500 mr-2">⚠️</div>
                          <div>
                            <h3 className="text-sm font-medium text-red-800">{copy.errors.title}</h3>
                            <p className="text-sm text-red-600 mt-1">{errors.submit}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full gradient-green text-white hover:opacity-90 animate-pulse-glow hover:animate-wiggle"
                      disabled={isSubmitting || isSubmitted}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {copy.submit.loading}
                        </>
                      ) : isSubmitted ? (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          {copy.submit.success}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {copy.submit.idle}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Company Information */}
              <Card className="border-0 shadow-lg animate-slide-in-right">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">{copy.info.companyTitle}</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4 animate-fade-in">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{copy.info.brand}</div>
                        <div className="font-semibold text-gray-900">Intelevia</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 animate-fade-in">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{copy.info.company}</div>
                        <div className="font-semibold text-gray-900">Guangzhou Wenyuanshiye Information Technology Co., Ltd.</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 animate-fade-in">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{copy.info.location}</div>
                        <div className="font-semibold text-gray-900">{copy.info.city}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp Chat - Hidden */}
              {false && (
                <Card className="border-0 shadow-lg animate-slide-in-right">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">WhatsApp Chat</h3>
                    <p className="text-gray-600 mb-6">
                      Get instant support and answers to your questions through WhatsApp:
                    </p>
                    
                    <div className="flex items-start space-x-4 animate-fade-in mb-6">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse-glow hover:animate-wiggle">
                        <MessageCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
            <div className="font-semibold text-gray-900">WhatsApp Support</div>
            <div className="text-green-600 font-medium">
              Scan QR code or click to start chatting
            </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="flex justify-center">
                      <button 
                        onClick={handleWhatsAppClick}
                        className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span>Open WhatsApp</span>
                      </button>
                    </div>

                    {/* QR Code Display - Progressive Loading */}
                    <div className="mt-6 flex flex-col items-center space-y-4">
                      <div className="bg-white p-6 rounded-lg shadow-md border-2 border-green-100 relative">
                        {/* Loading Skeleton - Show when not loaded */}
                        {!qrCodeLoaded && (
                          <div className="w-64 h-64 flex items-center justify-center">
                            <div className="relative">
                              {/* Spinning loader */}
                              <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
                              {/* Pulse effect */}
                              <div className="absolute inset-0 w-16 h-16 border-4 border-green-100 rounded-full animate-ping"></div>
                            </div>
                          </div>
                        )}
                        
                        {/* QR Code Image with Progressive Loading */}
                        <img 
                          src="/contact.webp" 
                          alt={t("accessibility.whatsapp")}
                          className={`w-64 h-64 object-contain transition-all duration-500 ${
                            qrCodeLoaded 
                              ? 'opacity-100 scale-100' 
                              : 'opacity-0 scale-95'
                          }`}
                          loading="lazy"
                          onLoad={handleQrCodeLoad}
                          onError={handleQrCodeError}
                        />
                      </div>
                      
                      {/* Description with fade-in effect */}
                      <p className={`text-sm text-gray-500 text-center transition-opacity duration-500 ${
                        qrCodeLoaded ? 'opacity-100' : 'opacity-0'
                      }`}>
                        Scan this QR code with your phone to start chatting on WhatsApp
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* General Inquiries */}
              <Card className="border-0 shadow-lg animate-slide-in-right">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">{copy.info.generalTitle}</h3>
                  <p className="text-gray-600 mb-6">
                    {copy.info.generalBody}
                  </p>
                  
                  <div className="flex items-start space-x-4 animate-fade-in">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse-glow hover:animate-wiggle">
                      <Mail className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{copy.info.emailLabel}</div>
                      <div className="text-emerald-600 font-medium">info@intelevia.com</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Business & Distribution Partnerships */}
              <Card className="border-0 shadow-lg animate-slide-in-right">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">{copy.info.partnershipTitle}</h3>
                  <p className="text-gray-600 mb-6">
                    {copy.info.partnershipBody}
                  </p>
                  
                  <div className="flex items-start space-x-4 animate-fade-in">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse-glow hover:animate-wiggle">
                      <Mail className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{copy.info.partnershipLabel}</div>
                      <div className="text-emerald-600 font-medium">business@intelevia.com</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
